import React, { useContext } from "react";
import { Link } from "react-router-dom";
import MainFrame from "./MainFrame";
import CtaBand from "./common/CtaBand";
import { GERMAN } from "../actions/language";
import { LanguageContext } from "../context/LanguageContext";

const Home = () => {
    const { language } = useContext(LanguageContext);
    const de = language === GERMAN;

    const services = [
        {
            index: '01',
            title: de ? 'Büroservice' : 'Serviço de Escritório',
            text: de
                ? 'Komplette Vorbereitung Ihrer Unterlagen für den Steuerberater, Lohnbuchhaltung und digitale Aufbereitung – strukturiert, sorgfältig, termingerecht.'
                : 'Preparação completa dos seus documentos para o consultor fiscal, contabilidade salarial e preparação digital – estruturada, cuidadosa e atempada.',
            items: de
                ? ['Vorbereitung für den Steuerberater', 'Lohnbuchhaltung & Abrechnungen', 'Digitale Belegaufbereitung']
                : ['Preparação para o consultor fiscal', 'Contabilidade salarial', 'Preparação digital de documentos'],
            link: '/office-service'
        },
        {
            index: '02',
            title: de ? 'Hilfestellungen' : 'Apoio',
            text: de
                ? 'Behördengänge, Unternehmensgründung, internationale Expansion – wir begleiten Sie durch jeden Schritt, in Deutschland und Portugal.'
                : 'Questões oficiais, criação de empresas, expansão internacional – acompanhamo-lo em cada passo, na Alemanha e em Portugal.',
            items: de
                ? ['Behörden & Formalitäten', 'Unternehmensgründung', 'Umzug DE ↔ PT & Übersetzungen']
                : ['Autoridades & formalidades', 'Criação de empresas', 'Mudança DE ↔ PT & traduções'],
            link: '/assistance'
        },
        {
            index: '03',
            title: de ? 'Verwaltung' : 'Administração',
            text: de
                ? 'Rechnungen, Angebote und Briefverkehr – wir halten Ihre administrative Kommunikation professionell am Laufen.'
                : 'Faturas, orçamentos e correspondência – mantemos a sua comunicação administrativa a funcionar de forma profissional.',
            items: de
                ? ['Rechnungen nach Vorgabe', 'Professionelle Angebote', 'Verwaltung des Briefverkehrs']
                : ['Faturas por especificação', 'Orçamentos profissionais', 'Gestão da correspondência'],
            link: '/administration'
        }
    ];

    const steps = [
        {
            title: de ? 'Kennenlernen' : 'Primeiro contacto',
            text: de
                ? 'Sie schildern uns Ihre Situation – telefonisch, per E-Mail oder persönlich in Bodenheim. Unverbindlich und kostenlos.'
                : 'Descreve-nos a sua situação – por telefone, e-mail ou pessoalmente em Bodenheim. Sem compromisso e gratuito.'
        },
        {
            title: de ? 'Analyse & Angebot' : 'Análise & proposta',
            text: de
                ? 'Wir prüfen, wo wir Sie entlasten können, und erstellen ein maßgeschneidertes Angebot – transparent und ohne versteckte Kosten.'
                : 'Analisamos onde o podemos apoiar e elaboramos uma proposta personalizada – transparente e sem custos escondidos.'
        },
        {
            title: de ? 'Umsetzung' : 'Implementação',
            text: de
                ? 'Wir übernehmen – zuverlässig, termingerecht und mit festen Ansprechpartnern. Sie konzentrieren sich auf Ihr Geschäft.'
                : 'Nós assumimos – de forma fiável, pontual e com interlocutores fixos. Concentre-se no seu negócio.'
        }
    ];

    return (
        <div>
            <MainFrame/>

            {/* Editorial-Intro */}
            <section className="tm-section">
                <div className="tm-container">
                    <div className="tm-intro">
                        <div>
                            <span className="tm-eyebrow">
                                {de ? 'Herzlich willkommen' : 'Bem-vindo'}
                            </span>
                            <p className="tm-intro__statement">
                                {de
                                    ? <span>Maßgeschneiderte Lösungen, <em>persönliche Betreuung</em> und ein Netzwerk, auf das Sie sich verlassen können.</span>
                                    : <span>Soluções personalizadas, <em>acompanhamento pessoal</em> e uma rede em que pode confiar.</span>}
                            </p>
                        </div>
                        <div className="tm-intro__body">
                            <p>
                                {de
                                    ? 'Als erfahrenes Dienstleistungsunternehmen sind wir Ihr verlässlicher Partner für Büroservice, umfassende Hilfestellungen und professionelle Verwaltungsaufgaben. Mit unserem breiten Angebot unterstützen wir Sie dabei, Ihren Arbeitsalltag effizienter zu gestalten und Ihre Ziele erfolgreich zu erreichen.'
                                    : 'Enquanto prestador de serviços experiente, somos o seu parceiro de confiança para serviços de escritório, assistência abrangente e tarefas administrativas profissionais. Com a nossa vasta gama de serviços, ajudamo-lo a organizar o seu dia a dia de forma mais eficiente e a atingir os seus objetivos.'}
                            </p>
                            <p>
                                {de
                                    ? 'Ob Organisation von Büroprozessen, Unterstützung bei komplexen Anliegen oder Optimierung administrativer Abläufe – bei uns stehen Qualität, Verlässlichkeit und Kundenzufriedenheit an erster Stelle.'
                                    : 'Quer se trate de organizar processos de escritório, de prestar apoio em questões complexas ou de otimizar fluxos administrativos – a qualidade, a fiabilidade e a satisfação do cliente são as nossas prioridades.'}
                            </p>
                            <p className="tm-intro__signature">
                                {de ? '— Ihr Team von Tomé & Martins GbR' : '— A sua equipa Tomé & Martins GbR'}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Leistungen */}
            <section className="tm-section tm-section--tight" id="leistungen">
                <div className="tm-container">
                    <div className="tm-section__head">
                        <span className="tm-eyebrow">{de ? 'Unsere Leistungen' : 'Os nossos serviços'}</span>
                        <h2 className="tm-section__title">
                            {de
                                ? <span>Drei Bereiche, <em>ein Anspruch</em>: Sie entlasten.</span>
                                : <span>Três áreas, <em>um objetivo</em>: o seu alívio.</span>}
                        </h2>
                    </div>
                    <div className="tm-services">
                        {services.map((service) => (
                            <div key={service.index} className="tm-service-card">
                                <span className="tm-service-card__index">{service.index}</span>
                                <h3 className="tm-service-card__title">{service.title}</h3>
                                <p className="tm-service-card__text">{service.text}</p>
                                <ul className="tm-service-card__list">
                                    {service.items.map((item, i) => <li key={i}>{item}</li>)}
                                </ul>
                                <Link to={service.link} className="tm-service-card__link">
                                    {de ? 'Mehr erfahren' : 'Saiba mais'} <i className="fas fa-arrow-right"/>
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Brücke Deutschland–Portugal */}
            <section className="tm-section tm-section--tight">
                <div className="tm-container">
                    <div className="tm-bridge">
                        <div className="tm-bridge__grid">
                            <div className="tm-bridge__country">
                                <span>{de ? 'Standort' : 'Localização'}</span>
                                <h3>Deutschland</h3>
                                <p>
                                    {de
                                        ? 'Unser Büro in Bodenheim bei Mainz – Ihr direkter Draht zu Steuerberatern, Fachanwälten und Behörden in Deutschland.'
                                        : 'O nosso escritório em Bodenheim, perto de Mainz – a sua ligação direta a consultores fiscais, advogados e autoridades na Alemanha.'}
                                </p>
                            </div>
                            <div className="tm-bridge__divider">
                                <i className="fas fa-exchange-alt"/>
                            </div>
                            <div className="tm-bridge__country">
                                <span>{de ? 'Netzwerk' : 'Rede'}</span>
                                <h3>Portugal</h3>
                                <p>
                                    {de
                                        ? 'Partner, Sprachkenntnisse und Behördenerfahrung vor Ort – ideal für Expansion, Umzug oder grenzüberschreitende Projekte.'
                                        : 'Parceiros, competências linguísticas e experiência com as autoridades locais – ideal para expansão, mudança ou projetos transfronteiriços.'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Prozess */}
            <section className="tm-section">
                <div className="tm-container">
                    <div className="tm-section__head">
                        <span className="tm-eyebrow">{de ? 'So arbeiten wir' : 'Como trabalhamos'}</span>
                        <h2 className="tm-section__title">
                            {de
                                ? <span>In drei Schritten zur <em>spürbaren Entlastung</em>.</span>
                                : <span>Em três passos para um <em>alívio real</em>.</span>}
                        </h2>
                    </div>
                    <div className="tm-steps">
                        {steps.map((step, i) => (
                            <div key={i} className="tm-step">
                                <div className="tm-step__inner">
                                    <h3>{step.title}</h3>
                                    <p>{step.text}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <CtaBand/>
        </div>
    );
}

export default Home;



// WEBPACK FOOTER //
// ./src/components/Home.js